import {
  type PdfCurrencyBlockType,
  type PdfCurrencyDisplay,
  pdfCurrencyDisplayOptions,
  resolvePdfCurrencyDisplay,
} from "@repo/shared/types";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./currency-display-field.i18n";

type CurrencyDisplayFieldProps = {
  blockType: PdfCurrencyBlockType;
  /** Absent on a component saved before the setting existed: the block's default. */
  value: PdfCurrencyDisplay | undefined;
  onChange: (value: PdfCurrencyDisplay) => void;
  /**
   * The block as configured draws only a chart (a forecast or expected value
   * with its table switched off). A block that never has a table is chart-only
   * through its options already.
   */
  chartOnly?: boolean;
};

export function CurrencyDisplayField({
  blockType,
  value,
  onChange,
  chartOnly: configuredChartOnly = false,
}: CurrencyDisplayFieldProps) {
  const t = useT(T);
  const options = pdfCurrencyDisplayOptions(blockType);
  const current = resolvePdfCurrencyDisplay(blockType, value);
  // Without a "With amounts" option the block only draws a chart.
  const chartOnly = configuredChartOnly || !options.includes("with_amounts");
  const optionLabel = (option: PdfCurrencyDisplay) =>
    option === "note" && chartOnly ? t.noteChart : t[option];
  const help =
    current === "with_amounts" && chartOnly ? t.with_amountsChartHelp : t[`${current}Help`];
  const id = `${blockType}-pdf-currency-display`;

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{t.currency}</Label>
      <Select value={current} onValueChange={(next: PdfCurrencyDisplay) => onChange(next)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {optionLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{help}</p>
    </div>
  );
}
